import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { Role } from 'models/RoleModel';
import { MultiSelectList, SingleSelectList } from 'components/common/MultiSelectList';

const RoleForm: React.FC = () => {
  const width = { all: 8, view: 8, open: 8, create: 8, modify: 8, delete: 8, print: 8, allloc: 8, expert: 8, history: 8, admin: 8 };
  const Columns: [string, number, number, string][] = [
    ['All', 10, width.all,'Include All'],
    ['View', 11, width.view, 'View Records List'],
    ['Open', 12, width.open, 'Open Records'],
    ['Create', 13, width.create, 'Create Records'],
    ['Modify', 14, width.modify, 'Modify Records'],
    ['Print', 16, width.print, 'Print Records'],
    ['History', 22, width.history, 'Show History Privileges'],
    ['Delete*', 15, width.delete, 'Delete Records'],
    ['Expert*', 20, width.expert, 'Export Privileges'],
    ['Admin*', 24, width.admin, 'Admin Privileges'],
    ['ALC*', 25, width.allloc, 'ALC'],
  ];
  const [formId, formName] = [Code.Form.Id.ROLE, Code.Form.Name.ROLE];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [formData, setFormData] = useState<Role>({});
  
  const [privilegeList, setPrivilegeList] = Hooks.Form.Options();
  const [specialList, setSpecialList] = Hooks.Form.Options();
  const [reportList, setReportList] = Hooks.Form.Options();
  const [documentList, setDocumentList] = Hooks.Form.Options();
  const [groupList, setGroupList] = Hooks.Form.Options();
  const [menuList, setMenuList] = Hooks.Form.Options();
  
  const [selectedPrivilege, setSelectedPrivilege] = useState<Set<string>>(new Set());
  const [selectedSpecial, setSelectedSpecial] = useState<Set<string>>(new Set());
  const [selectedReport, setSelectedReport] = useState<Set<string>>(new Set());
  const [selectedDocument, setSelectedDocument] = useState<Set<string>>(new Set());
  const [selectedGroup, setSelectedGroup] = useState<Set<string>>(new Set());
  const [selectedMenu, setSelectedMenu] = useState<Set<string>>(new Set());
  
  const [privilegeFilter, setPrivilegeFilter] = useState<string>('');
  const [specialFilter, setSpecialFilter] = useState<string>('');
  const [reportFilter, setReportFilter] = useState<string>('');
  const [documentFilter, setDocumentFilter] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [menuFilter, setMenuFilter] = useState<string>('');

  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.FORM, Code.Option.Common.NOTHING, setPrivilegeList, setError,true);
    Files.Json.API(Code.File.SPECIAL, Code.Option.Common.NOTHING, setSpecialList, setError,true);
    Files.Json.API(Code.File.REPORT, Code.Option.Common.NOTHING, setReportList, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.DOCUMENT_TYPE, Code.Option.Common.NOTHING, setDocumentList, setError);
    Files.Json.API(Code.File.GROUP, Code.Option.Common.NOTHING, setGroupList, setError,true);
    Files.Json.API(Code.File.FORM, Code.Option.Common.NOTHING, setMenuList, setError,true);
    
    API.Data.GetByKeyId<Role>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.role_active); 
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
        }));
        setSelectedPrivilege(Utility.Convert.SetString(data.privilege_list))
        setSelectedSpecial(Utility.Convert.SetString(data.special_list))
        setSelectedReport(Utility.Convert.SetString(data.report_list))
        setSelectedDocument(Utility.Convert.SetString(data.document_list))
        setSelectedGroup(Utility.Convert.SetString(data.group_list))
        setSelectedMenu(Utility.Convert.SetString(data.menu_list))
      }})
      setLoading(false);
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;

    const finalData = Utility.Sanitize.FormData({
      ...formData,
      company_id: Utility.Storage.CompanyId(),
      location_id: Utility.Storage.LocationId(),
      entry_id: Utility.Storage.UserId(),
      privilege_list: JSON.stringify(Array.from(selectedPrivilege)),
      special_list: JSON.stringify(Array.from(selectedSpecial)),
      report_list: JSON.stringify(Array.from(selectedReport)),
      document_list: JSON.stringify(Array.from(selectedDocument)),
      group_list: JSON.stringify(Array.from(selectedGroup)),
      menu_list: JSON.stringify(Array.from(selectedMenu)),
    });

    API.Data.Save(formName, keyId, finalData, setMessage)

  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId}  title={formData.role_name} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)}  formName={formName} Privilege={Privilege} />
          <Control.Tab.Header titles={["Details", "Privilege", "Special", "Reports", "Document", "Group", "Menu"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
 
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Text id={Name.Role.Name} value={formData.role_name} title={Title.FirstName} onChange={(e) => Form.Field.Change(e, formData, setFormData)} require={true} />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Role.Comment} value={formData.role_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              <MultiSelectList allFormList={privilegeList} selectedItem={selectedPrivilege} setSelectedItem={setSelectedPrivilege}  listFilter={privilegeFilter} setListFilter={setPrivilegeFilter} subOptions={Columns} titleWidth={20} />
              <Field.Title title="* indicates privileges are excluded from all" />
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab3} isActive={activeTab === Code.Tab.Tab3}>
              <SingleSelectList allFormList={specialList} selectedItem={selectedSpecial} setSelectedItem={setSelectedSpecial}  listFilter={specialFilter} setListFilter={setSpecialFilter} />
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab4} isActive={activeTab === Code.Tab.Tab4}>
              <SingleSelectList allFormList={reportList} selectedItem={selectedReport} setSelectedItem={setSelectedReport}  listFilter={reportFilter} setListFilter={setReportFilter} />
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab5} isActive={activeTab === Code.Tab.Tab5}>
              <SingleSelectList allFormList={documentList} selectedItem={selectedDocument} setSelectedItem={setSelectedDocument}  listFilter={documentFilter} setListFilter={setDocumentFilter} />
            </Control.Tab.Content>
           
            <Control.Tab.Content id={Code.Tab.Tab6} isActive={activeTab === Code.Tab.Tab6}>
              <SingleSelectList allFormList={groupList} selectedItem={selectedGroup} setSelectedItem={setSelectedGroup}  listFilter={groupFilter} setListFilter={setGroupFilter} />
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab7} isActive={activeTab === Code.Tab.Tab7}>
              <SingleSelectList allFormList={menuList} selectedItem={selectedMenu} setSelectedItem={setSelectedMenu}  listFilter={menuFilter} setListFilter={setMenuFilter} />
            </Control.Tab.Content>

          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default RoleForm;